import { getDisplayQuantity, getEstimateNumberValueBasedOnSettings, getMeasurementDetails } from "@Actions/estimation";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import { useRef } from "react";
import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { RootState } from "@Reducers/index";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema";
import type { ColumnDefinition, GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";

/** A derived-measurement row: one viewer markup plus its raw measurement value. */
type DerivedMeasurementRow = GridRowBase & {
  id: string | number;
  _name: string;
  _measurementValue: number;
  _unitMultiplier: number;
  units?: string;
};

/** The SlickGrid formatter signature, bound to this grid's row type and to one column's field. */
type DerivedMeasurementFormatter<K extends keyof DerivedMeasurementRow> = NonNullable<
  ColumnDefinition<DerivedMeasurementRow, K>["formatter"]
>;

type DeriveMeasurementsProps = {
  dataSource: DerivedMeasurementRow[];
  derivedMeasurementsSelectingEvents: (records: DerivedMeasurementRow[]) => void;
  selectedMeasurements: DerivedMeasurementRow[];
};

/**
 * Picks the display unit whose dimension matches a measurement's `type`. All five units — and their
 * `factor` / `type` / `selectedSymbol` members — are `.optional()` on the estimate response
 * (`displayUnitSerializedSchema` in estimates.schema.ts), so an estimate that has not had every
 * dimension configured really does reach here with holes. `findLast` keeps the original precedence:
 * the five sequential `if`s this replaced let the last matching dimension win.
 */
const matchingDisplayUnit = (estimate: EstimateData, type: string | null) =>
  [
    estimate.linearDisplayUnit,
    estimate.areaDisplayUnit,
    estimate.volumeDisplayUnit,
    estimate.countDisplayUnit,
    estimate.angleDisplayUnit,
  ].findLast((unit) => unit?.type?.name === type);

const DeriveMeasurements = ({
  dataSource,
  derivedMeasurementsSelectingEvents,
  selectedMeasurements,
}: DeriveMeasurementsProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<SlickGrid1Ref<DerivedMeasurementRow> | null>(null);
  const estimateData = useSelector((state: RootState) => state.estimateData);

  const templateHelper = (name: string) => getMeasurementDetails(name);

  const handleRowSelection = () => {
    // KNOWN BUG, preserved verbatim: the grid ref is dereferenced with no guard here, though
    // `derivedMeasurementsDataBound` below checks it.
    const grid = gridRef.current!.slickGrid;
    // slickgrid declares `getDataItem` as `any`, and it yields `undefined` for a selected row index
    // that no longer maps to a row (a selection kept across a refresh). Read at the widened type so
    // the holes stay visible, then drop them — the `.filter` below is the guard that was already here.
    const selectedRecords: Array<DerivedMeasurementRow | undefined> = grid
      .getSelectedRows()
      .map((rowIndex: number) => grid.getDataItem(rowIndex));
    derivedMeasurementsSelectingEvents(selectedRecords.filter((record) => record !== undefined));
  };

  const derivedMeasurementsDataBound = () => {
    if (selectedMeasurements.length && gridRef.current) {
      const rowIndices: number[] = [];
      dataSource.forEach((measurement, index: number) => {
        if (selectedMeasurements.find((record) => record.id == measurement.id)) {
          rowIndices.push(index);
        }
      });
      // The `gridRef.current` guard above does not survive into this callback.
      gridRef.current.dataView.onRowsChanged.subscribe(function () {
        gridRef.current!.slickGrid.setSelectedRows(rowIndices);
      });
    }
  };

  const deriveMeasurementsNameTemplate: DerivedMeasurementFormatter<"_name"> = (data, value, a, b, params) =>
    templateHelper(params._name).name;

  const deriveMeasurementsQuantityTemplate: DerivedMeasurementFormatter<"_measurementValue"> = (
    data,
    val,
    a,
    b,
    params,
  ) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    const { type } = templateHelper(params._name);
    // No configured unit for this dimension leaves the quantity unscaled, as `factor = 1` did.
    const factor = matchingDisplayUnit(estimateData, type)?.factor ?? 1;

    const { quantity } = getDisplayQuantity({
      quantity: params._measurementValue * params._unitMultiplier,
      factor,
    });
    root.render(<div>{getEstimateNumberValueBasedOnSettings(quantity, estimateData, "measurement")}</div>);
    return container;
  };

  const deriveMeasurementsUnitTemplate: DerivedMeasurementFormatter<"units"> = (data, value, a, b, params) => {
    const { type } = templateHelper(params._name);
    return matchingDisplayUnit(estimateData, type)?.selectedSymbol?.name;
  };

  // Built through `typedColumns`, the only constructor that really checks each `field` against the
  // row type. These columns carried no `id` before, which left slickgrid keying all three on the
  // same `undefined`.
  const deriveMeasurementColumns = typedColumns<DerivedMeasurementRow>([
    {
      id: "_name",
      field: "_name",
      name: t("Name"),
      formatter: deriveMeasurementsNameTemplate,
      textAlign: "Left",
    },
    {
      id: "_measurementValue",
      field: "_measurementValue",
      name: t("Measurement"),
      formatter: deriveMeasurementsQuantityTemplate,
      textAlign: "Left",
    },
    {
      id: "units",
      field: "units",
      name: t("Units"),
      formatter: deriveMeasurementsUnitTemplate,
      textAlign: "Left",
    },
  ]);

  return (
    <div className="overflow-auto h-full w-full">
      <SlickGrid1
        ref={gridRef}
        showPageCount={false}
        columnDefinitions={deriveMeasurementColumns}
        rowHeight={50}
        dataset={dataSource}
        onSelectedRowIdsChanged={handleRowSelection}
        onReactGridCreated={derivedMeasurementsDataBound}
        hideNoDataIllustration
      />
    </div>
  );
};

export default DeriveMeasurements;
